<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { AUTHMODELHS } from '@/utils/models'
import { RECAPTCHA_KEYS } from '@/utils/recaptcha'
import recaptchaService from '@/services/recaptcha.service'
import utilModelsService from '@/services/util.models.service'
import { useAuthStore } from '@/stores/auth.store'
import authService, { type IForgotPasswordRequest } from '@/services/auth.service'
import closeIcon from '@/assets/images/svg/AuthOnboarding/CloseButton.svg'
import BaseInput from '@/components/forms/BaseInput.vue'
import LoginRegisterBanner from '@/components/header/auth/AuthOnboardingAdvBanner/LoginRegisterBanner.vue'
import AuthOnboardingLabel from '@/components/header/auth/AuthOnboardingLabel.vue'
import Button from '@/components/atoms/button/index.vue'
import ModelLayoutAuth from '@/layouts/ModelLayoutAuth.vue'
import { until } from '@vueuse/core'
import { useField, useForm } from 'vee-validate'
import { VueRecaptcha } from 'vue-recaptcha'
import { string } from 'yup'
import { handleError } from '@/services/errorHandler.service'

const authStore = useAuthStore()
const route = useRoute()

const { errors, handleSubmit, resetForm } = useForm()

const { value: email, errorMessage: emailError } = useField('email', string().required().email())

const colorTheme = ref<string>('light')
const sitekey = ref<string | null>(RECAPTCHA_KEYS.RECAPTCHALOGINSITEKEY)
const rechaptha = ref<any>(null)
const recapthaToken = ref<string>('')
const recapthaError = ref<string>('')
const showRecaptcha = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const httpError = ref<string>('')
const httpSuccess = ref<string>('')

const canSubmit = computed(() => {
  return Object.keys(errors.value).length === 0 && !isLoading.value
})

/**
 * Handles the response from the reCAPTCHA verification and sets the token.
 * @param response - The reCAPTCHA response token.
 */
const recaptchaVerified = (response: string) => {
  recapthaToken.value = response
  recapthaError.value = ''
}

/**
 * Handles the reCAPTCHA error and sets the error message.
 * @param error - The reCAPTCHA error message.
 */
const recaptchaError = (error: string) => {
  recapthaToken.value = ''
  recapthaError.value = error
  rechaptha.value.reset()
}

/**
 * If prop is true, page retrieves data again from API after changes from modal
 */
const handleLoadDataWhenClose = () => {
  authStore.setRedirectTo(null)
}

const onSubmit = handleSubmit(async (values) => {
  authStore.setUserEmailForOTPVerified(values.email)
  isLoading.value = true
  rechaptha.value.execute()

  await until(recapthaToken).changed()

  if (!recapthaToken.value || recapthaError.value) {
    return
  }

  const requestPayload: IForgotPasswordRequest = {
    email: values.email,
    gc: recapthaToken.value
  }

  await authService
    .forgotPassword(requestPayload)
    .then((response: any) => {
      httpSuccess.value =
        response?.data?.message ||
        'Please check your mail for Password reset Link (JDoodle sends mail only if the entered mail is already registered)'
      httpError.value = ''
      resetForm()
      // close the modal
      utilModelsService.closeModal(AUTHMODELHS.FORGOTPASSWORD)
      utilModelsService.openModal(AUTHMODELHS.OTP)
    })
    .catch((error: any) => {
      httpSuccess.value = ''
      if (error?.response?.data.errorCode == 'OTP_ALREADY_GENERATED') {
        httpError.value =
          'OTP has already been generated. Please wait sometime before generating again'
      } else {
        httpError.value = handleError(error)
      }
    })
    .finally(() => {
      recapthaToken.value = ''
      rechaptha.value.reset()
      isLoading.value = false
    })
})
onBeforeMount(async () => {
  sitekey.value = await recaptchaService.getRechapthaKey(RECAPTCHA_KEYS.RECAPTCHALOGINSITEKEY)
})
onMounted(async () => {
  await utilModelsService.onOpenModel((hs: string | null) => {
    if (hs === AUTHMODELHS.FORGOTPASSWORD && !showRecaptcha.value) {
      showRecaptcha.value = true
    }
  })
})
</script>

<template>
  <ModelLayoutAuth :hs="AUTHMODELHS.FORGOTPASSWORD">
    <div class="flex h-screen w-full flex-col">
      <div class="absolute right-0 flex justify-end">
        <img
          :src="closeIcon"
          alt="Close"
          class="link-primary hs-collapse-toggle mr-5 mt-2 h-[20px] w-[20px] cursor-pointer transition-all"
          :data-hs-overlay="`#${AUTHMODELHS.FORGOTPASSWORD}`"
          @click="handleLoadDataWhenClose"
          v-show="!(route.name === 'extensionLogin' && !route.query.register)"
        />
      </div>
      <div class="flex w-full flex-1 p-1 sm:p-5">
        <div class="hidden w-full rounded-3xl bg-[#EBEFF6] md:hidden lg:block lg:min-w-[45%]">
          <LoginRegisterBanner
            text="Conduct live coding sessions with your team."
            highlighertext="live coding sessions"
          />
        </div>

        <div class="mt-10 h-full w-full rounded-md sm:mt-8 lg:min-w-[50%]">
          <div class="flex items-center justify-center">
            <AuthOnboardingLabel
              title="Reset your password"
              description="Can't remember your password? No worries! Enter your registered email to start the reset process."
              customstyle="mt-5
              text-sm font-normal text-center"
              customstyleTitle="text-center text-3xl font-medium leading-10"
            />
          </div>
          <div class="p-5">
            <form @submit.prevent="onSubmit" class="sm:pt-12">
              <div class="flex w-full flex-col items-center justify-center">
                <div class="w-full lg:w-[500px]">
                  <BaseInput
                    label="Email address"
                    :inputType="'email'"
                    placeholder="joe@example.com"
                    autocomplete="email"
                    :error="emailError"
                    v-model="email"
                    class="mb-2"
                    :isBoldLabel="true"
                    :isLightMode="true"
                    :isLightGray="false"
                  >
                  </BaseInput>
                </div>
                <p :class="['p-xsmall ', 'error', { hidden: !recapthaError }]">
                  {{ recapthaError }}
                </p>
                <p :class="['p-small', 'text-center', 'text-sky-950']" v-if="isLoading">
                  Checking your details. Please wait...
                </p>
                <p :class="['p-small', 'error', { hidden: !httpError }]">
                  {{ httpError }}
                </p>
                <p :class="['p-small', 'text-center', 'text-sky-950', { hidden: !httpSuccess }]">
                  {{ httpSuccess }}
                </p>
              </div>
              <vue-recaptcha
                v-if="showRecaptcha"
                size="invisible"
                ref="rechaptha"
                :theme="colorTheme === 'dark' ? 'dark' : 'light'"
                :sitekey="sitekey || ''"
                @verify="recaptchaVerified"
                @fail="recaptchaError('Recaptcha failed. Please verify again.')"
                @error="recaptchaError"
              />

              <div class="mt-9 flex justify-center">
                <Button
                  :disabled="!canSubmit"
                  color="secondary"
                  variant="filled"
                  size="medium"
                  type="submit"
                  class="mt-6 h-[48px] sm:w-[171px]"
                >
                  Send OTP
                </Button>
              </div>
            </form>

            <div class="mt-3 text-center text-sm font-medium leading-tight dark:text-neutral-300">
              Remember your password?
              <Button
                type="button"
                color="secondary"
                variant="link"
                class="ml-1 p-0 font-semibold"
                :data-hs-overlay="`#${AUTHMODELHS.LOGIN}`"
              >
                Log In
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- form -->
  </ModelLayoutAuth>
</template>
